<template>
  <div>
    <div
      v-if="tipCardNotAvailable"
      :style="{ height: '100vh' }"
      :data-cy="`tip-personnel-member-empty-state`"
      class="d-flex flex-column justify-center align-center white"
    >
      <div class="top-buttons">
        <v-btn
          class="lang-button"
          icon
          :ripple="false"
          data-cy="language-selector-button"
          @click="openLanguageSelector"
        >
          <img :src="languageImage" style="width: 22px" />
        </v-btn>
      </div>
      <StateScreen
        :title="$t('screens.tipPersonnelMember.empty.title')"
        :subtitle="$t('screens.tipPersonnelMember.empty.subtitle')"
        :image="require(`@/assets/images/states/sad-mascot.png`)"
      />
      <div class="d-flex pt-10 px-10 w-100">
        <Button dark @click="refreshPage">
          {{ $t('screens.tipPersonnelMember.empty.cta') }}
        </Button>
        <LocaleSelector
          ref="languageSelector"
          :language-list="languageList"
          :selected-language="this.$i18n.locale()"
          @changeLanguage="changeLanguage"
        />
      </div>
    </div>
    <div v-else>
      <div
        v-if="isLoading"
        :style="{ height: '100vh' }"
        class="py-6 d-flex justify-center align-center"
      >
        <v-progress-circular indeterminate color="black" />
      </div>
      <form v-show="!isLoading" id="payment-form" @submit="onPayCtaPress">
        <SpecialOccasionBanner :locale="waiterLocale || ''" />
        <LayoutCard v-if="tipsRecipient" style="border-radius: 0 0 20px 20px">
          <TipsHeader :color="tipsColorGlobal" />
          <div v-if="!orderId" class="top-buttons">
            <v-btn
              class="lang-button"
              icon
              :ripple="false"
              data-cy="language-selector-button"
              @click="openLanguageSelector"
            >
              <img :src="languageImage" style="width: 22px" />
            </v-btn>
          </div>
          <EasyWithGreet
            style="
              position: absolute;
              top: 26px;
              left: 50%;
              transform: translate(-50%, -50%);
            "
          />
          <div class="d-flex flex-row justify-end pb-6">
            <v-icon v-if="!!orderId" @click="goBack"> mdi-close </v-icon>
          </div>
          <div class="d-flex flex-column align-center text-center mt-5">
            <Avatar
              :photo-url="tipsRecipient.photo"
              :name="tipsRecipient.name"
              :lazy-src="tipsRecipient.lazySrc"
              data-cy="personnel-member-avatar"
              :size="100"
            />
            <h2 class="pt-3" data-cy="personnel-member-name">
              {{ tipsRecipient.name }}
            </h2>
            <div
              class="tp-text-body pt-3"
              data-cy="personnel-member-description"
            >
              {{ tipsRecipient.description }}
            </div>
            <PersonnelBottomSheetPicker
              :personnel-list="personnelList"
              :default-open="isStaffMembersSelectMode"
              @pick="pickPersonnel"
            />
            <ReviewDrawer
              v-if="tipsRecipient.show_review_cta_in_payment_screen"
              :tips-qr-card-hash="tipsQrCardHash"
            >
              <template v-slot:activator="{ toggle }">
                <a
                  class="primary--text"
                  data-cy="leave-review-cta"
                  @click="toggle"
                >
                  {{ $t('components.review.leaveReviewCta') }}
                </a>
              </template>
            </ReviewDrawer>
          </div>
          <h4 class="pt-10 pb-3 text-center" data-cy="tip-title">
            {{ $t('screens.tipPersonnelMember.tipTitle') }}
          </h4>
          <TipsPicker
            v-if="tipsRecipient.name"
            :tips="tipChips"
            :color="tipsColorGlobal"
            :min="minTipAmount"
            :max="maxTipAmount"
            @on-change="onTipsChange"
          />
          <div class="py-8">
            <div v-if="tipAmount" class="d-flex justify-space-between pb-2">
              <span class="tp-text-body">
                {{
                  this.$t('screens.tipPersonnelMember.subtotal', {
                    name: tipsRecipient.name,
                  })
                }}
              </span>
              <span class="tp-text-body">
                {{ tipAmountFormatted }}
              </span>
            </div>
            <div
              v-if="transactionFee"
              class="d-flex justify-space-between pb-2"
            >
              <span class="tp-text-body" data-cy="tip-transaction-fee">
                {{ this.$t('screens.tipPersonnelMember.transactionFee') }}
              </span>
              <span class="tp-text-body" data-cy="tip-transaction-fee-amount">
                {{ feeFormatted }}
              </span>
            </div>
            <div class="d-flex justify-space-between">
              <span class="tp-text-subtitle font-weight-medium">
                {{ this.$t('screens.tipPersonnelMember.total') }}
              </span>
              <span
                class="tp-text-subtitle font-weight-medium"
                data-cy="tip-total-amount"
              >
                {{ totalFormatted }}
              </span>
            </div>
          </div>
          <v-expand-transition>
            <div v-show="tipAmount">
              <PaymentMethods
                ref="paymentMethods"
                :color="tipsColorGlobal"
                :wallet-available="walletAvailable"
                :payment-methods-override="paymentMethodsOverride"
                :preselected-payment-method="presentPaymentMethod"
                @on-change="onPaymentMethodChange"
                @on-success="goToSuccess"
                @on-error="onPaymentError"
              />
            </div>
          </v-expand-transition>
        </LayoutCard>
        <div class="px-4 pb-16">
          <v-alert
            v-show="errorMessage && tipAmount"
            border="left"
            colored-border
            color="red accent-4"
            class="mt-7"
            dismissible
            close-icon="mdi-close"
            >{{ errorMessage }}</v-alert
          >
          <v-expand-transition>
            <div v-show="showPayCta && tipAmount" class="pt-3 px-6">
              <Button
                ref="CTAButton"
                block
                dark
                :disabled="isLoadingPayments"
                type="submit"
                data-cy="tip-cta"
              >
                {{ $t('screens.tipPersonnelMember.payCta') }}
              </Button>
            </div>
          </v-expand-transition>
        </div>
      </form>
      <PaymentStatusBackdrop ref="backdrop" />
      <v-overlay v-show="!isLoading" :value="updatingTipAmount">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </div>
    <LocaleSelector
      ref="languageSelector"
      :language-list="languageList"
      :selected-language="this.$i18n.locale()"
      @changeLanguage="changeLanguage"
    />
    <PaymentBottomSheetError
      ref="paymentBottomSheetError"
      @paymentErrorBottomSheetCtaPress="generatePaymentIntents(tipAmount)"
    />
  </div>
</template>

<script>
import LayoutCard from '@/components/LayoutCard.vue';
import TipsPicker from '@/components/Cart/TipsPicker.vue';
import PaymentMethods from '@/components/Payments/PaymentMethods.vue';
import Button from '@/components/Button.vue';
import PaymentStatusBackdrop from '@/components/Payments/PaymentStatusBackdrop.vue';
import Avatar from '@/components/common/Avatar.vue';
import StateScreen from '@/components/StateScreen.vue';
import EasyWithGreet from '@/components/EasyWithGreet.vue';
import TipsHeader from '@/components/Tips/TipsHeader.vue';
import LocaleSelector from '@/components/AgnosticLocaleSelector.vue';
import PersonnelBottomSheetPicker from '@/components/Tips/PersonnelBottomSheetPicker.vue';
import ReviewDrawer from '@/components/Reviews/ReviewDrawer.vue';
import PaymentBottomSheetError from '@/components/Payments/PaymentBottomSheetError.vue';
import { compact } from 'lodash';
import SpecialOccasionBanner from '@/components/common/SpecialOccasionBanner.vue';
import { parseQueryParam } from '@/utils/strings';

import { PAYMENT_METHODS } from '../utils/constants';

export default {
  components: {
    TipsPicker,
    LayoutCard,
    PaymentMethods,
    Button,
    PaymentStatusBackdrop,
    Avatar,
    StateScreen,
    EasyWithGreet,
    TipsHeader,
    LocaleSelector,
    PersonnelBottomSheetPicker,
    ReviewDrawer,
    PaymentBottomSheetError,
    SpecialOccasionBanner,
  },
  data() {
    return {
      isLoading: true,
      isLoadingPayments: false,
      stripe: undefined,
      tipAmount: null,
      paymentMethod: null,
      walletAvailable: false,
      errorMessage: null,
      paymentRequest: null,
      elements: null,
      tipsQrCardHash: null,
      orderId: null,
      sessionToken: null,
      total: 0,
      transactionFee: 0,
      updatingTipAmount: false,
      neopayPaymentIntent: null,
      stripePaymentUuid: null,
    };
  },
  computed: {
    session() {
      return this.$store.state.session;
    },
    paymentMethodsOverride() {
      return [
        PAYMENT_METHODS.SOLID_WALLET,
        PAYMENT_METHODS.SOLID_CARD,
        PAYMENT_METHODS.NEOPAY,
      ];
    },
    tipsRecipient() {
      return this.$store.getters.tipsRecipientData;
    },
    totalFormatted() {
      return this.$store.getters.getFormattedPrice(this.total);
    },
    feeFormatted() {
      return this.$store.getters.getFormattedPrice(this.transactionFee);
    },
    tipAmountFormatted() {
      return this.$store.getters.getFormattedPrice(this.tipAmount);
    },
    minTipAmount() {
      return this.$store.getters.getMinAmount;
    },
    maxTipAmount() {
      return this.$store.getters.getMaxAmount;
    },
    tipAmounts() {
      return this.$store.getters.getTipsAmounts || [];
    },
    preselectedTipIndex() {
      return this.$store.getters.getPreselectedTipIndex || 0;
    },
    tipChips() {
      const formatPrice = (amount) => {
        return this.$store.getters.getFormattedPrice(amount, 0);
      };

      return this.tipAmounts.map((amount, index) => {
        return {
          value: amount,
          selected: index === this.preselectedTipIndex,
          label: formatPrice(amount),
        };
      });
    },
    showPayCta() {
      return (
        this.paymentMethod === PAYMENT_METHODS.NEOPAY ||
        this.paymentMethod === PAYMENT_METHODS.SOLID_CARD
      );
    },
    languageImage() {
      const code = this.$i18n.locale();

      return require(`@/assets/images/flags/${code}.svg`);
    },
    locale() {
      return this.$i18n.locale();
    },
    waiterLocale() {
      return this.$store.getters.getWaiterLocale;
    },
    languageList() {
      const originalWaiterLocale = this.waiterLocale;

      if (this.tipCardNotAvailable) {
        return ['en', 'lt', 'lv'];
      }

      const languages = ['en'];

      // Original waiter locale and english are always available
      return compact([originalWaiterLocale, ...languages]);
    },
    personnelList() {
      return this.$store.getters.getPersonnelList;
    },
    selectedPersonnelId() {
      return this.$store.getters.getSelectedPersonnelId;
    },
    isStaffMembersSelectMode() {
      return this.$store.getters.getIsStaffMembersSelectMode;
    },
    tipCardNotAvailable() {
      return this.$store.getters.getTipCardNotAvailable;
    },
    redirectUrl() {
      return `${process.env.VUE_APP_BASE_URL}/tip/success/${this.tipsQrCardHash}?orderId=${this.orderId}&sessionToken=${this.sessionToken}&personnel_id=${this.selectedPersonnelId}&paymentUuid=${this.stripePaymentUuid}&lang=${this.locale}`;
    },
    presentPaymentMethod() {
      return PAYMENT_METHODS.SOLID_WALLET;
    },
  },
  watch: {
    async tipAmount(newTipAmount, oldTipAmount) {
      if (oldTipAmount !== newTipAmount && !!newTipAmount) {
        this.generatePaymentIntents(newTipAmount);
      }
    },
    async selectedPersonnelId(newPersonnelId, oldPersonnelId) {
      if (newPersonnelId !== oldPersonnelId) {
        this.generatePaymentIntents(this.tipAmount);
      }
    },
    async tipCardNotAvailable(newTipCardNotAvailable) {
      if (newTipCardNotAvailable) {
        this.changeLanguage({ code: 'en' });
      }
    },
  },
  async mounted() {
    this.isLoading = true;
    const tipsQrCardHash = this.$route.params.tipsQrCardHash;
    const orderId = this.$route.query.orderId;
    const sessionToken = this.$route.query.sessionToken;

    this.tipsQrCardHash = tipsQrCardHash;
    this.orderId = orderId;
    this.sessionToken = sessionToken;

    if (sessionToken) {
      await this.$store.dispatch('fetchSessionData', { session: sessionToken });
    }

    await this.$store.dispatch('fetchTipsData', { tipsQrCardHash });
    await this.switchToQueryLanguageIfSupported();

    this.isLoading = false;
  },
  methods: {
    async switchToQueryLanguageIfSupported() {
      const queryLanguage = parseQueryParam(this.$route.query.lang)?.toLowerCase();

      if (!queryLanguage) {
        return;
      }

      if (!this.languageList.includes(queryLanguage)) {
        return;
      }

      await this.changeLanguage({ code: queryLanguage });
    },
    async generatePaymentIntents(newTipAmount) {
      if (!newTipAmount) {
        return;
      }

      this.updatingTipAmount = true;

      const [paymentRequestResponse, neoPaymentResponse] = await Promise.all([
        this.generateSolidTipPaymentIntent(newTipAmount),
        this.createNeopayTransaction({ amount: newTipAmount }),
      ]);

      this.neopayPaymentIntent = neoPaymentResponse;

      this.total = paymentRequestResponse.total;
      this.transactionFee = paymentRequestResponse.transaction_fee;
      this.stripePaymentUuid = paymentRequestResponse.uuid;
      this.stripeClientSecret = paymentRequestResponse.clientSecret;

      this.updatingTipAmount = false;
    },
    async generateSolidTipPaymentIntent(newTipAmount) {
      return this.$store.dispatch('generateSolidTipPaymentIntent', {
        tipsQrCardHash: this.tipsQrCardHash,
        amount: newTipAmount,
      });
    },
    async createNeopayTransaction({ amount = null } = {}) {
      return this.$store.dispatch('generateNeoPaymentIntent', {
        amount,
        tipsQrCardHash: this.tipsQrCardHash,
      });
    },
    goBack() {
      this.$router.go(-1);
    },
    refreshPage() {
      window.location.reload();
    },
    async onTipsChange(amount) {
      this.tipAmount = amount;
    },
    async goToSuccess(clientSecret) {
      await this.showBackdrop('success', 2000);

      this.$router.replace({
        name: 'TipSuccess',
        params: {
          tipsQrCardHash: this.tipsQrCardHash,
        },
        query: {
          payment_intent_client_secret: clientSecret,
          orderId: this.orderId,
          sessionToken: this.sessionToken,
          personnel_id: this.selectedPersonnelId,
          paymentUuid: this.stripePaymentUuid,
          lang: this.locale,
        },
      });
    },
    onPaymentMethodChange(method) {
      if (method === PAYMENT_METHODS.NEOPAY) {
        setTimeout(this.scrollToPayCTA, 300);
        this.transactionFee = this.neopayPaymentIntent.transaction_fee;
        this.total = this.neopayPaymentIntent.total;
      } else if (method === PAYMENT_METHODS.SOLID_CARD) {
        const solidGatePaymentIntent =
          this.$store.getters.solidGatePaymentIntent;
        this.total = solidGatePaymentIntent.total;
        this.transactionFee = solidGatePaymentIntent.transaction_fee;
        setTimeout(this.scrollToPayCTA, 300);
      }

      this.paymentMethod = method;
    },
    async onPayCtaPress(e) {
      e.preventDefault();

      if (this.isLoadingPayments) {
        return;
      }

      if (this.paymentMethod === PAYMENT_METHODS.NEOPAY) {
        await this.payByNeopay(e);
        return;
      } else if (this.paymentMethod === PAYMENT_METHODS.SOLID_CARD) {
        // Sketchy way to submit the form
        this.$refs.paymentMethods.$refs?.solidCardForm?.submit();
      }
    },
    onPaymentError(error) {
      const errorCodeMap = {
        3.02: 'insufficient_funds',
      };

      const adoptedError = {
        ...error,
        decline_code: errorCodeMap[error.code] || 'generic',
      };

      this.hideBackdrop();
      this?.$refs?.paymentBottomSheetError?.open(adoptedError);
    },
    payByNeopay() {
      if (this.neopayPaymentIntent.redirect_url) {
        this.isLoading = true;
        window.location = this.neopayPaymentIntent.redirect_url;
      }
    },
    async scrollToPayCTA() {
      await new Promise((resolve) => setTimeout(resolve, 100));
      this.$refs.CTAButton.$el.scrollIntoView({ behavior: 'smooth' });
    },
    async showBackdrop(type, duration) {
      await this.$refs.backdrop.show(type, duration);
    },
    async hideBackdrop() {
      await this.$refs.backdrop.hide();
    },
    openLanguageSelector() {
      this.$refs.languageSelector.open();
    },
    async changeLanguage(language) {
      const translations = (await import(`../i18n/${language.code}.json`))
        .default;

      this.$i18n.add(language.code, translations);
      this.$i18n.set(language.code);
      this.$store.dispatch('setSelectedLanguage', language.code);
    },
    pickPersonnel(recipientId) {
      this.$store.dispatch('setTipsRecipient', { recipientId });
    },
  },
};
</script>

<style lang="scss" scoped>
.top-buttons {
  position: absolute;
  top: 0;
  right: 0;
  padding: 15px;
  z-index: 100;
}

.lang-button {
  background-color: rgb(255 255 255 / 70%) !important;
  box-shadow: none !important;
  transition: background-color 100ms ease-in-out;
}
</style>
